import { ReactNode } from 'react';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { startCase } from 'lodash-es';
import { PhoneCallsTypes } from '@frontend/api-phone-calls';
import { useTranslation, i18next, Trans } from '@frontend/i18n';
import { PhonesUtils } from '@frontend/phone-panels';
import { OUTCOME_MAPPING, getAiOutcomeIcon as getIcon } from '@frontend/phone-ui';
import { theme } from '@frontend/theme';
import { Chip, Text, Button, PopoverDialog, usePopoverDialog } from '@frontend/design-system';

export const AiCallCreditInfoModalTrigger = () => {
  const { t } = useTranslation('calls');
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({});

  return (
    <>
      <Button {...getTriggerProps()} iconName='info' variant='secondary' css={{ padding: 0, height: 16, width: 16 }} />
      <PopoverDialog {...getDialogProps()}>
        <Trans t={t}>
          <div style={{ display: 'grid', gap: theme.spacing(1), width: 370 }}>
            <Text size='large' weight='bold'>
              AI Call Credit
            </Text>

            <Text size='medium'>
              Calls are counted toward your monthly call credit usage when the interaction with the AI Receptionist is
              at least 20 seconds and the caller engages in the conversation.
            </Text>
          </div>
        </Trans>
      </PopoverDialog>
    </>
  );
};

export const AiOutcomeInfoModalTrigger = () => {
  const { t } = useTranslation('calls');
  // const { modalProps, triggerProps } = useModalControl();
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({
    // placement: 'top-start',
  });

  return (
    <>
      <Button {...getTriggerProps()} iconName='info' variant='secondary' css={{ padding: 0, height: 16, width: 16 }} />
      <PopoverDialog {...getDialogProps()}>
        <Trans t={t}>
          <div style={{ display: 'grid', gap: theme.spacing(1), width: 370 }}>
            <Text size='large' weight='bold'>
              AI Call Outcome
            </Text>

            <div>
              <Text size='medium' weight='bold'>
                What It Helps With
              </Text>
              <Text size='medium'>
                Determine if the AI Receptionist was able to handle the call on its own or if it needed to involve your
                office staff.
              </Text>
            </div>
            <div style={{ display: 'grid', gap: theme.spacing(1) }}>
              <Text size='medium' weight='bold'>
                How It's Defined
              </Text>
              <div style={{ display: 'grid', gap: theme.spacing(1) }}>
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_END_CALL]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_ROUTE_MISSED]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_FATAL]} />
                <OutcomeDisplay config={AI_OUTCOME_CONFIG[AIOutcome.AI_OUTCOME_INCOMPLETE]} />
              </div>
            </div>
          </div>
        </Trans>
      </PopoverDialog>
    </>
  );
};

export const ResultInfoModalTrigger = () => {
  const { t } = useTranslation('calls');
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>();

  return (
    <>
      <Button {...getTriggerProps()} iconName='info' variant='secondary' css={{ padding: 0, height: 16, width: 16 }} />
      <PopoverDialog {...getDialogProps()}>
        <Trans t={t}>
          <div style={{ display: 'grid', gap: theme.spacing(1), width: 370 }}>
            <Text size='large' weight='bold'>
              Call Result
            </Text>

            <div>
              <Text size='medium' weight='bold'>
                What It Helps With
              </Text>
              <Text size='medium'>
                Assess how well incoming calls are being handled by your staff or by your AI Receptionist.
              </Text>
            </div>
            <div style={{ display: 'grid', gap: theme.spacing(1) }}>
              <div>
                <Text size='medium' weight='bold'>
                  How It's Defined
                </Text>
                <Text size='medium'>
                  The call result reflects the{' '}
                  <Text as='span' size='medium' weight='bold'>
                    final status
                  </Text>{' '}
                  of the call. For example, if the AI answers but your staff misses the transfer, the call result will
                  show as Missed.
                </Text>
              </div>
              <div style={{ display: 'grid', gap: theme.spacing(1), gridTemplateColumns: 'auto 1fr' }}>
                <ResultDisplay config={RESULT_CONFIG['answered']} />
                <ResultDisplay config={RESULT_CONFIG['missed']} />
                <ResultDisplay config={RESULT_CONFIG['abandoned']} />
                <ResultDisplay config={RESULT_CONFIG['forwarded']} />
              </div>
            </div>
          </div>
        </Trans>
      </PopoverDialog>
    </>
  );
};

type OutcomeConfig = {
  outcome: (typeof OUTCOME_MAPPING)[keyof typeof OUTCOME_MAPPING];
  text: string;
  secondaryText?: ReactNode;
};

const AI_OUTCOME_CONFIG: Record<string, OutcomeConfig> = {
  [AIOutcome.AI_OUTCOME_END_CALL]: {
    outcome: OUTCOME_MAPPING.SUCCESS,
    text: i18next.t('Successfully assisted the patient: ', { ns: 'calls' }),
    secondaryText: i18next.t('The AI Receptionist successfully handled the call on its own.', { ns: 'calls' }),
  },
  [AIOutcome.AI_OUTCOME_FATAL]: {
    outcome: OUTCOME_MAPPING.FATAL,
    text: i18next.t('Error: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The AI Receptionist{' '}
        <Text as='span' weight='bold' size='medium'>
          could not answer
        </Text>{' '}
        the call or{' '}
        <Text as='span' weight='bold' size='medium'>
          could not finish
        </Text>{' '}
        assisting the caller. This can happen when the set up is incomplete or when the system is experiencing a
        temporary disruption.
      </Trans>
    ),
  },
  [AIOutcome.AI_OUTCOME_ROUTE_MISSED]: {
    outcome: OUTCOME_MAPPING.ROUTE_MISSED,
    text: i18next.t('Needs attention: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The AI Receptionist transferred the call to your office, but the{' '}
        <Text as='span' weight='bold' size='medium'>
          transfer was missed
        </Text>{' '}
        . The caller may still need assistance.
      </Trans>
    ),
  },
  [AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED]: {
    outcome: OUTCOME_MAPPING.ROUTE_ANSWERED,
    text: i18next.t('Call transferred: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The AI Receptionist{' '}
        <Text as='span' weight='bold' size='medium'>
          handed the conversation off{' '}
        </Text>
        for further assistance, either by transferring the caller to office staff or by moving the conversation to text.
        This can happen when the request isn’t supported or when the caller asks to speak with staff.
      </Trans>
    ),
  },
  [AIOutcome.AI_OUTCOME_INCOMPLETE]: {
    outcome: OUTCOME_MAPPING.INCOMPLETE,
    text: i18next.t('Incomplete: ', { ns: 'calls' }),
    secondaryText: (
      <Trans ns='calls'>
        The caller{' '}
        <Text as='span' weight='bold' size='medium'>
          hung up{' '}
        </Text>
        before the AI Receptionist could determine the outcome.
      </Trans>
    ),
  },
};

const OutcomeDisplay = ({ config }: { config: OutcomeConfig }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    }}
  >
    <div style={{ justifySelf: 'end', alignSelf: 'start', display: 'grid' }}>
      {getIcon({ outcome: config.outcome })}
    </div>
    <div>
      <Text as='span' size='medium' weight='bold'>
        {config.text}
      </Text>
      {config.secondaryText && (
        <Text as='span' size='medium'>
          {config.secondaryText}
        </Text>
      )}
    </div>
  </div>
);

type ResultConfig = {
  result: PhoneCallsTypes.CallLog['Status'];
  text: string;
};

const RESULT_CONFIG: Record<PhoneCallsTypes.CallLog['Status'], ResultConfig> = {
  answered: {
    result: 'answered',
    text: i18next.t('The call was answered by your staff or the AI Receptionist. ', { ns: 'calls' }),
  },
  missed: {
    result: 'missed',
    text: i18next.t('The call was not answered by your staff or by the AI Receptionist, and it went to voicemail.', {
      ns: 'calls',
    }),
  },
  abandoned: {
    result: 'abandoned',
    text: i18next.t('The caller hung up before the call could be answered or go to voicemail.', { ns: 'calls' }),
  },
  forwarded: {
    result: 'forwarded',
    text: i18next.t('The call was forwarded externally to another phone number, but the call was not answered.', {
      ns: 'calls',
    }),
  },
};

const ResultDisplay = ({ config }: { config: ResultConfig }) => {
  const statusChip = PhonesUtils.getStatusChipColor(config.result);

  return (
    <div
      style={{
        display: 'grid',
        gridColumn: 'span 3',
        gridTemplateColumns: 'subgrid',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Chip isResponsive variant={statusChip} css={{ justifySelf: 'start', alignSelf: 'start', display: 'grid' }}>
        {startCase(config.result)}
      </Chip>
      <Text as='span' size='medium'>
        {config.text}
      </Text>
    </div>
  );
};
